import React, { useEffect, useRef, useState } from "react";
import { notify } from "../Notification";
import { FILTERS, LIBRARY } from "../../data/spotify";
import { BookmarkIcon, ExpandIcon, HeartIcon, ListIcon, NoteIcon, PinIcon, PlusIcon, SearchIcon } from "./icons";

// Liked Songs' cover: Spotify's gradient with a heart.
export const LikedCover = ({ className = "" }) => (
  <span className={`sp-liked-cover ${className}`} aria-hidden="true">
    <HeartIcon />
  </span>
);

// Spotify's placeholders: a note for a playlist with no cover, a bookmark for Your Episodes.
const PLACEHOLDERS = { note: NoteIcon, episodes: BookmarkIcon };

export const Cover = ({ item, className = "" }) => {
  if (item.image) return <img src={item.image} alt="" draggable={false} className={`sp-cover ${className}`} />;
  const Icon = PLACEHOLDERS[item.icon];
  return Icon ? (
    <span className={`sp-placeholder sp-placeholder--${item.icon} ${className}`} aria-hidden="true">
      <Icon />
    </span>
  ) : (
    <LikedCover className={className} />
  );
};

// Your Library: filters, a search of its own, and the saved playlists, albums and podcasts. The
// one playing shows in green; the open one is highlighted.
const Library = ({ page, playingId, onOpen }) => {
  const [filter, setFilter] = useState(null);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const field = useRef(null);

  useEffect(() => {
    if (searching && field.current) field.current.focus();
  }, [searching]);

  const q = query.trim().toLowerCase();
  const items = LIBRARY.filter(
    (item) =>
      (!filter || FILTERS[filter].includes(item.kind)) && (!q || `${item.title} ${item.by}`.toLowerCase().includes(q)),
  );

  const open = (item) =>
    item.open ? onOpen(item.open) : notify(`${item.title} plays in Spotify itself. ICEMAN and Liked Songs play here.`);

  return (
    <aside className="sp-panel sp-library" aria-label="Your Library">
      <header className="sp-library-head">
        <h2 className="sp-library-title">Your Library</h2>
        <span className="sp-create" aria-hidden="true">
          <PlusIcon />
          Create
        </span>
        <span className="sp-icon-button" aria-hidden="true">
          <ExpandIcon />
        </span>
      </header>
      <div className={`sp-chips ${scrolled ? "is-scrolled" : ""}`} role="group" aria-label="Filter">
        {Object.keys(FILTERS).map((name) => (
          <button
            type="button"
            key={name}
            className={`sp-chip ${filter === name ? "is-on" : ""}`}
            aria-pressed={filter === name}
            onClick={() => setFilter((f) => (f === name ? null : name))}
          >
            {name}
          </button>
        ))}
      </div>
      {/* The search and sort scroll away with the list; the filters stay. */}
      <div className="sp-library-scroll" onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 0)}>
        <div className="sp-library-tools">
          {searching ? (
            <label className="sp-library-search">
              <SearchIcon />
              <input
                ref={field}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onBlur={() => !query && setSearching(false)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setQuery("");
                    setSearching(false);
                  }
                }}
                placeholder="Search in Your Library"
                aria-label="Search in Your Library"
                spellCheck={false}
              />
            </label>
          ) : (
            <button
              type="button"
              className="sp-icon-button"
              onClick={() => setSearching(true)}
              aria-label="Search in Your Library"
            >
              <SearchIcon />
            </button>
          )}
          <span className="sp-sort" aria-hidden="true">
            Recents
            <ListIcon />
          </span>
        </div>
        <ul className="sp-library-list">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={`sp-library-item ${item.open === page ? "is-open" : ""} ${
                  item.id === playingId ? "is-playing" : ""
                }`}
                onClick={() => open(item)}
              >
                <Cover item={item} className="sp-library-cover" />
                <span className="sp-library-text">
                  <span className="sp-library-name">{item.title}</span>
                  <span className="sp-library-sub">
                    {item.pinned && <PinIcon className="sp-pin" aria-label="Pinned" />}
                    {item.kind} • {item.by}
                  </span>
                </span>
              </button>
            </li>
          ))}
          {!items.length && (
            <li className="sp-library-empty">
              {q ? `Couldn't find "${query.trim()}"` : `No ${filter.toLowerCase()} yet`}
            </li>
          )}
        </ul>
      </div>
    </aside>
  );
};

export default Library;
